import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ImagePlus } from "lucide-react";
import { useRef, useState } from "react";

import { api, toApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { keys } from "@/lib/queries";
import type { SiteTheme } from "@/lib/types";
import { ImageField } from "./ImageField";
import { Button, ErrorBanner, FormSection, Input, Spinner } from "./ui";

type Photo = { id: number; image: string; caption: string; position: number };
type Change =
  | { kind: "upload"; files: File[] }
  | { kind: "update"; id: number; file?: File; caption?: string }
  | { kind: "delete"; id: number }
  | { kind: "cover"; id: number }
  | { kind: "reorder"; ids: number[] };
const galleryKey = ["site-gallery"];

export function SiteGallery({ onCover }: { onCover: (cover: string | null) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [validation, setValidation] = useState("");
  const [saved, setSaved] = useState(false);
  const query = useQuery({
    queryKey: galleryKey,
    queryFn: async () => (await api.get<Photo[]>("/site/gallery/")).data,
  });
  const photos = query.data ?? [];
  const mutation = useMutation({
    mutationFn: async (change: Change) => {
      setSaved(false);
      if (change.kind === "upload") {
        // One request per image stays within the proxy's upload limit.
        for (const file of change.files) {
          const body = new FormData();
          body.append("image", file);
          await api.post("/site/gallery/", body);
        }
      } else if (change.kind === "update") {
        const body = new FormData();
        if (change.file) body.append("image", change.file);
        if (change.caption !== undefined) body.append("caption", change.caption);
        await api.patch(`/site/gallery/${change.id}/`, body);
      } else if (change.kind === "delete") {
        await api.delete(`/site/gallery/${change.id}/`);
      } else if (change.kind === "cover") {
        const { data } = await api.post<SiteTheme>(`/site/gallery/${change.id}/cover/`);
        qc.setQueryData(keys.site, data);
        onCover(data.cover);
      } else {
        await api.post("/site/gallery/reorder/", { ids: change.ids });
      }
    },
    onSuccess: () => setSaved(true),
    onSettled: () => qc.invalidateQueries({ queryKey: galleryKey }),
  });

  function upload(files: File[]) {
    setValidation("");
    if (!files.length) return;
    if (files.length + photos.length > 6) return setValidation(t("gallery.limit"));
    if (files.some((file) => !["image/jpeg", "image/png", "image/webp"].includes(file.type))) {
      return setValidation(t("gallery.formats"));
    }
    if (files.some((file) => file.size > 5 * 1024 * 1024)) {
      return setValidation(t("gallery.size"));
    }
    mutation.mutate({ kind: "upload", files });
  }

  function move(index: number, offset: number) {
    const ids = photos.map((photo) => photo.id);
    [ids[index], ids[index + offset]] = [ids[index + offset], ids[index]];
    mutation.mutate({ kind: "reorder", ids });
  }

  return (
    <FormSection title={t("gallery.title")} description={t("gallery.hint")}>
      {query.isLoading ? <Spinner /> : query.isError ? (
        <><ErrorBanner message={t("site.loadFailed")} />
          <Button type="button" variant="secondary" onClick={() => query.refetch()}>{t("gallery.retry")}</Button></>
      ) : (
        <fieldset disabled={mutation.isPending || query.isFetching} className="min-w-0 space-y-4 disabled:opacity-60">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-steel-dim">{photos.length}/6 · {t("gallery.optional")}</span>
            <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp"
              className="hidden" aria-label={t("gallery.add")}
              onChange={(event) => {
                upload(Array.from(event.target.files ?? []));
                event.target.value = "";
              }} />
            <Button type="button" variant="secondary" disabled={photos.length >= 6}
              onClick={() => input.current?.click()}><ImagePlus size={16} /> {t("gallery.add")}</Button>
          </div>
          {!photos.length && <p className="border border-dashed border-line p-6 text-sm text-steel-dim">{t("gallery.empty")}</p>}
          <div className="space-y-4">
            {photos.map((photo, index) => (
              <div key={photo.id} className="space-y-3 border border-line bg-carbon-900/40 p-4">
                <ImageField label={`${t("gallery.photo")} ${index + 1}`} value={photo.image} file={null}
                  onPick={(file) => file && mutation.mutate({ kind: "update", id: photo.id, file })}
                  onRemove={() => mutation.mutate({ kind: "delete", id: photo.id })} />
                <Caption key={`${photo.id}:${photo.caption}`} photo={photo}
                  onSave={(caption) => mutation.mutate({ kind: "update", id: photo.id, caption })} />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="secondary" disabled={index === 0}
                    aria-label={`${t("gallery.up")} ${index + 1}`} onClick={() => move(index, -1)}><ArrowUp size={14} /></Button>
                  <Button type="button" size="sm" variant="secondary" disabled={index === photos.length - 1}
                    aria-label={`${t("gallery.down")} ${index + 1}`} onClick={() => move(index, 1)}><ArrowDown size={14} /></Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => mutation.mutate({ kind: "cover", id: photo.id })}>
                    {t("gallery.cover")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </fieldset>
      )}
      {validation && <ErrorBanner message={validation} />}
      {mutation.isError && <ErrorBanner message={toApiError(mutation.error).message} />}
      <div role="status" className="text-sm text-steel-dim">
        {mutation.isPending ? t("gallery.saving") : saved ? t("site.published") : t("gallery.autosave")}
      </div>
    </FormSection>
  );
}

function Caption({ photo, onSave }: { photo: Photo; onSave: (value: string) => void }) {
  const t = useT();
  const [caption, setCaption] = useState(photo.caption);
  return <div className="flex flex-wrap items-end gap-2">
    <div className="min-w-0 flex-1"><Input label={t("gallery.caption")} maxLength={160}
      value={caption} onChange={(event) => setCaption(event.target.value)}
      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); onSave(caption); } }} /></div>
    <Button type="button" size="sm" variant="secondary" disabled={caption === photo.caption}
      onClick={() => onSave(caption)}>{t("gallery.saveCaption")}</Button>
  </div>;
}
